import type { SelectOptionType } from '../../Controls/Select/types';

export interface CollapsedChip {
  /** The option node the chip displays — a group node or a leaf. */
  option: SelectOptionType;
  /**
   * Values from the current selection this chip covers. Removing the chip
   * filters ALL of them out in one transaction — for a group chip that
   * includes selected-but-disabled leaves and (if present) the group's own
   * value, so no hidden residue keeps filtering.
   */
  covered: Array<string | number>;
}

export interface CollapsedSelection {
  chips: CollapsedChip[];
  /**
   * Selected values no chip covers — values missing from the option tree
   * (async options still loading, stale persisted values) or present only on
   * unlabeled nodes. Rendered as per-value fallback chips.
   */
  residual: Array<string | number>;
}

/**
 * Collapses a SELECT_MULTI selection against its option tree for display:
 * a group whose enabled leaves are ALL selected renders as ONE chip, at any
 * depth — the maximal fully-selected subtree wins. Ports the legacy
 * FilterSummary rollup (`mapOptions`/`areAllChildrenSelected`) and matches
 * the MultiSelect checkbox rule (`disabled || checked`), so the summary shows
 * a group chip exactly when the dropdown shows the group's checkbox checked.
 *
 * Display-time only: the stored value stays leaf-shaped; group option values
 * never enter the selection through this path. A group node directly present
 * in the selection (a `SHOW_PARENT` checked strategy) also collapses to its
 * own chip. Walks `options` nesting — the shape MultiSelect renders
 * (`fieldNames: { children: 'options' }`). Assumes option values are unique
 * across the tree, the same contract the store's `findOption` /
 * `flattenOptionValues` already rely on.
 */
export function collapseSelection(
  options: SelectOptionType[],
  values: Array<string | number>,
): CollapsedSelection {
  const valueSet = new Set<unknown>(values);
  const chips: CollapsedChip[] = [];

  const allEnabledLeavesSelected = (node: SelectOptionType): boolean => {
    const kids = node.options;
    if (!kids?.length) {
      return node.value !== undefined && valueSet.has(node.value);
    }
    return kids.every((child) => child.disabled || allEnabledLeavesSelected(child));
  };

  const coveredUnder = (node: SelectOptionType): Array<string | number> => {
    const out: Array<string | number> = [];
    const walk = (current: SelectOptionType) => {
      if (current.value !== undefined && valueSet.has(current.value)) {
        out.push(current.value);
      }
      current.options?.forEach(walk);
    };
    walk(node);
    return out;
  };

  const visit = (node: SelectOptionType) => {
    const selfSelected = node.value !== undefined && valueSet.has(node.value);
    const groupFullySelected =
      !!node.options?.length && !node.disabled && allEnabledLeavesSelected(node);
    if (selfSelected || groupFullySelected) {
      const covered = coveredUnder(node);
      // covered can be empty for a vacuously "full" group (only disabled,
      // unselected leaves) — nothing to represent, keep descending.
      if (covered.length > 0 && node.label !== undefined) {
        chips.push({ option: node, covered });
        return;
      }
    }
    node.options?.forEach(visit);
  };
  options.forEach(visit);

  const represented = new Set(chips.flatMap((chip) => chip.covered));
  const residual = values.filter((v) => !represented.has(v));
  return { chips, residual };
}
